import React, {useEffect, useState, useMemo} from 'react';
import {registerBrick, Predicate, CloudObject, tagToString} from 'olympe';
import { ReactBrick, useProperty } from '@olympeio/core';
import { combineLatest } from 'rxjs';

import TreeView from '@mui/lab/TreeView';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CircularProgress from '@mui/material/CircularProgress';

import '../../../../res/stylesheets/systemTreeComponent.scss';

// IMPORT MODELS
import Component from '../../../models/Component';
import InspectionEntity from '../../../models/InspectionEntity';
import System from '../../../models/System';
import Observation from "../../../models/Observation";
import Unit from "../../../models/Unit";

// IMPORT COMPONENTS
import { RenderTree } from './RenderTree';

export default class SystemTree extends ReactBrick {
    /**
     * @override
     */
    setupExecution($) {
        return combineLatest([$.observe('system', false), $.observe('unit states', false), $.observe('unit')]);
    }

    /**
     * @override
     */
    static getReactComponent($) {
        return (props) => {
            const [system, unitStates, unit] = props.values;

            if (!system || !unit) {
                return SystemTree.getLoader();
            }

            const [expanded, setExpanded] = useState([]);
            const [checkedEntities, setCheckedEntities] = useState();
            const [checkedEntitiesForEditing, setCheckedEntitiesForEditing] = useState(new Map());
            const [evalEntities, setEvalEntities] = useState();

            const showUnavailable = useProperty($, 'show unavailable');
            const isExpandAll = useProperty($, 'is expand all');
            const showOnlyInspect = useProperty($, 'show only to inspect');
            const isInspectMode = $.get('Is Inspect Mode');
            const showCheckboxes = $.get('show checkboxes');

            const loading = !checkedEntities || (isInspectMode && !evalEntities);

            const editedAssessment = useProperty($, 'edit-assessment');

            // Set entities for edition
            useEffect(() => {
                if (editedAssessment) {
                    const entitiesForEditing = editedAssessment[tagToString(unit)];
                    setCheckedEntitiesForEditing(entitiesForEditing);
                }
            }, [editedAssessment]);

            // Subscribe to all existing observations
            useEffect(() => {
                const sub = unit
                    .follow(Unit.observationsRel).andReturn()
                    .follow(Observation.targetRel).andReturn()
                    .observe($)
                    .subscribe((results) => {
                        const entities = results.reduce((map, [obs, entity]) => map.set(entity.getTag(), obs), new Map());
                        setCheckedEntities(entities);
                    });
                return () => sub.unsubscribe();
            }, []);

            // Subscribe to all existing evaluations
            useEffect(() => {
                if (isInspectMode) {
                    const sub = unit.follow(Unit.observationsRel)
                        .follow(Observation.evalutationRel).andReturn()
                        .back()
                        .follow(Observation.targetRel).andReturn()
                        .observe($).subscribe((results) => {
                            setEvalEntities(results.reduce((map, [evals, entity]) => map.set(entity.getTag(), evals), new Map()))
                        });
                    return () => sub.unsubscribe();
                }
            }, []);

            // Build Full Tree Data, once
            const treeData = useMemo(() => {
                const rootComponents = system.follow(System.componentsRel).sortBy(Component.rankProp).executeFromCache();

                // Get subcomponents of the specified component
                const fetchSubComponents = (component) => {
                    const subComponents = component.follow(Component.subComponentsRel).sortBy(Component.rankProp).executeFromCache();
                    return fetchDataComponents(subComponents);
                };

                // Transform component data to tree item, and filter out component that are not available
                const fetchDataComponents = (components) => {
                    return components.size() > 0
                        ? components.map((component) => {
                            const children = fetchSubComponents(component);
                            let isAvailable = false;
                            if (unitStates) {
                                // Create the predicate to filter out components that are not available
                                const unitStatePredicate = Predicate.in(...unitStates.values());
                                const hasAvailableChild = children?.some((child) => child.isAvailable) || false;
                                isAvailable = hasAvailableChild || (component.query()
                                    .follow(Component.inspectionsRel)
                                    .follow(InspectionEntity.presumedStatesRel)
                                    .filter(unitStatePredicate)
                                    .executeFromCache().size() > 0);
                            }
                            const inspections = component.query()
                                .followRecursively(Component.subComponentsRel, true)
                                .follow(Component.inspectionsRel)
                                .executeFromCache().map((inspection) => inspection.getTag());
                            return { name: component.getName(), tag: component.getTag(), isAvailable, children, inspections };
                        }) : null;
                };

                return fetchDataComponents(rootComponents);
            }, [unitStates]);

            // Switch between (un)available / exclude (no) observation data depending on the showUnavailable boolean.
            const filteredDataTree = useMemo(() => {
                if (!checkedEntities) {
                    return [];
                }

                if (!showUnavailable && !isInspectMode) {
                    const excludeUnavailableChildren = (nodes) => nodes && nodes
                        .filter((node) => node?.isAvailable)
                        .map((node) => ({...node, children: excludeUnavailableChildren(node?.children)}));
                    return excludeUnavailableChildren(treeData);
                } else if (isInspectMode && showOnlyInspect) {
                    const excludeNoObservationChildren = (nodes) => nodes && nodes
                    .filter((node) => checkedEntities.has(node.tag))
                    .map((node) => ({...node, children: excludeNoObservationChildren(node?.children) }));
                    return excludeNoObservationChildren(treeData);
                } else {
                    return treeData;
                }
            }, [showUnavailable, showOnlyInspect, isInspectMode, checkedEntities]);

            const allNodes = useMemo(() => {
                const iterator = [...filteredDataTree];
                const res = [];
                for (const node of iterator) {
                    res.push(node.tag);
                    node.children?.forEach((child) => iterator.push(child));
                }
                return res;
            }, [filteredDataTree]);

            // Expand/Collapse All
            useEffect(() => {
                const expandedState = isExpandAll ? allNodes : [];
                setExpanded(expandedState);
            }, [isExpandAll, allNodes]);

            // When open a node to display children
            const handleToggle = (event, nodeIds) => {
                event.stopPropagation()
                // This is triggered when we click on the toggle, to avoid selecting the node itself.
                if (event.target.closest('.MuiTreeItem-iconContainer')) {
                    setExpanded(nodeIds);
                }
            };

            const handleNodeSelect = (event, componentTag) => {
                event.persist();
                // This is triggered when we click on the node itself only (not when toggling the item)
                if (!event.target.closest('.MuiTreeItem-iconContainer')) {
                    $.set('selected component', CloudObject.get(componentTag));
                    $.set('selected observation', checkedEntities.get(componentTag) ?? null);
                    $.trigger('go to component tab');
                }
            };

            const onCheckChange = (checkValue, nodes) => {
                $.set('triggered components', nodes.map((node) => CloudObject.get(node.tag)));
                $.trigger(checkValue ? 'create observations' : 'delete observations');
            };

            return (
                <>
                {loading ?  SystemTree.getLoader() : (
                    <div className='tree-view-container'>
                        <div className='tree-view-inner'>
                            {filteredDataTree?.length ? (
                                <TreeView
                                    defaultCollapseIcon={<ArrowDropDownIcon color='primary' fontSize='large'/>}
                                    defaultExpanded={allNodes}
                                    expanded={expanded}
                                    onNodeToggle={handleToggle}
                                    defaultExpandIcon={<ArrowRightIcon color='primary' fontSize='large'/>}
                                    onNodeSelect={handleNodeSelect}
                                >
                                    {filteredDataTree.map((node) => (
                                        <RenderTree
                                            key={node.tag}
                                            node={node}
                                            checkedComponents={checkedEntities}
                                            onCheckChange={onCheckChange}
                                            availablePredicate={(!showUnavailable && unitStates) ? Predicate.in(...unitStates.values()) : null}
                                            showCheckboxes={showCheckboxes}
                                            evalEntities={evalEntities}
                                            isInspectMode={isInspectMode}
                                            checkedEntitiesForEditing={checkedEntitiesForEditing}
                                        />
                                    ))}
                                </TreeView>
                            ) : null}
                        </div>
                    </div>
                    )}
                </>
            );
        };
    }

    /**
     * @private
     * @return {Element}
     */
    static getLoader() {
        return (<div
            style={{
                width: '100%',
                height: '100px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}>
            <CircularProgress />
        </div>)
    }
}

registerBrick('018967bd4f420a8fdf3a', SystemTree);
